import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import * as Sentry from '@sentry/react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import Big from 'big.js'
import { ethers } from 'ethers'
import useWeb3 from '../hooks/useWeb3'
import { selectUser } from '../modules/user/selectors'
import { useGetContractByNameQuery } from '../modules/web3/api'
import { BASE } from '../constants'
import { selectStakeInformation } from '../modules/staking/selectors'
import {
  useGetUniversalVaultVestingInformationQuery,
  useLazyGetStakeInformationQuery,
  useLazyGetTokensStakeInformationQuery,
} from '../modules/staking/api'
import { TierIndex } from '../types/reducerTypes'
import { convertAllStringsInObjectToBigInt } from '../utils/helpers'
import { setStakeInformation } from '../modules/staking/actions'
import { amountFromWei, amountToWei } from '../utils/amount'
import {
  useGetContractInstanceReadOnly,
  useGetContractInstanceWriteOnly,
} from '../page_components/Memecoins/useTokenContractValues'
import {useIsLoggedInAndAuthenticated} from '../hooks/useIsLoggedIn'
import useNetworkId from '../hooks/useNetworkId'
import { selectAccount } from '../modules/web3/selectors'

export const isTgeSoon = false //true//true //false // :skull"

const tierIndexMap = { 3: 0, 6: 1, 12: 2 }

const dailyRewards = 12950

// Types
export type UserRewards = {
  lastClaimedTimestamp: bigint
  standardPoolTokensAccrued: bigint
  bonusPoolTokensAccrued: bigint
  standardPoolTokensClaimed: bigint
  bonusPoolTokensClaimed: bigint
}

export type UserVaultStake = {
  tierIndex: number
  vaultsStaked: number
  stakedAt: bigint
  expiresAt: bigint
  unstakedAt: bigint
  previousStake: bigint
  autoRelock: boolean
}

export type UserTokenStake = {
  tierIndex: number
  tokensStaked: bigint
  stakedAt: bigint
  expiresAt: bigint
  unstakedAt: bigint
  previousStake: bigint
  autoRelock: boolean
}

export type SablierBalanceAndContract = {
  streamId: number
  balance: bigint
  lockerAddress: string
  label: string
  contract: ethers.Contract
}

export type TokensStakeInformation = {
  dailyRewards: bigint
  currentDayRewards: bigint
  totalTokensStaked: bigint
  totalStandardWeighting: bigint
}

type StakingContextType = {
  dailyRewards: number
  getTierApy: ({ tokensAmount, tier, target, totalTokensStaked }) => number
  getEstimatedExtraTokens: ({
    target,
    tokensAmount,
    tier,
    totalTokensStaked,
  }) => number
  stakeInformation: any
  tokensStakeInformation: { [key: TierIndex]: TokensStakeInformation }
  fetchVaultsState: () => Promise<void>
  userVaultsState: any
  isLoadingUserVaultsState: boolean
  userVaultsStateError: any
  userVaultStakes: UserVaultStake[]
  userTokenStakes: UserTokenStake[]
  hasClaimableVaults: boolean
  vaultsStaked: number
  vaultsClaimed: number
  vaultsAvailable: number
  vaultsHeld: number
  estimatedTokensPerVault: bigint
  stakedVaultsPercentage: number
  tokensAvailable: number
  tokensStaked: number
  claimableTokens: bigint
  totalTokensClaimed: bigint
  totalTokensVested: bigint
  totalTokensAccrued: bigint
  stakedTokensPercentage: number
  stakeVaults: (
    months: number,
    amount: number,
    autoRelock: boolean,
  ) => Promise<void>
  vaultsStakingStatus: 'idle' | 'staking' | 'success' | 'error'
  getEarlyUnstakeFee: (
    tokensStaked: bigint,
    timeStaked: number,
    expiresAt: number,
  ) => Promise<bigint>
  unstakeVaults: (tierIndex: number) => Promise<void>
  vaultsUnstakingStatus: 'idle' | 'unstaking' | 'success' | 'error'
  claimVaults: (amount: number) => Promise<void>
  vaultsClaimStatus: 'idle' | 'claiming' | 'success' | 'error'
  stakeTokens: (
    months: number,
    amount: number,
    autoRelock: boolean,
  ) => Promise<void>
  tokensStakingStatus: 'idle' | 'approve' | 'staking' | 'success' | 'error'
  unstakeTokens: (tierIndex: number) => Promise<void>
  tokensUnstakingStatus: 'idle' | 'unstaking' | 'success' | 'error'
  tokensClaimableFromRewards: bigint
  tokensClaimableFromVesting: bigint
  tokensClaimedFromRewards: bigint
  tokensClaimedFromVesting: bigint
  vestClaimedVaultsStatus: {
    status: 'idle' | 'vesting' | 'success' | 'error'
    amount?: bigint
  }
  vestClaimedVaults: (amount: bigint) => Promise<void>
  rewardsClaimStatus: {
    status: 'idle' | 'claiming' | 'success' | 'error'
    amount?: bigint
  }
  claimRewards: (amount: bigint) => Promise<void>
  hasSablierStream: boolean
  pendingSablierBalances: SablierBalanceAndContract[]
  claimSablierStream: (stream: SablierBalanceAndContract) => Promise<void>
  sablierClaimStatus: {
    status: 'idle' | 'claiming' | 'success' | 'error'
    amount?: bigint
  } & Partial<SablierBalanceAndContract>
  resetStatusByKind: (
    kind:
      | 'vaults-claim'
      | 'vaults-staking'
      | 'tokens-staking'
      | 'tokens-staked'
      | 'rewards-claimed'
      | 'vest-claimed'
      | 'sablier-claimed',
  ) => void
  tokenApy: { [key: TierIndex]: number }
  totalTokensStaked: bigint
  universalVaultVestingInformation: VaultVestingUniversalInformation
  calculateBonusApy: (tierIndex: number) => number
  getEstimatedTokensPerVaultOverStake: (tierIndex: number) => bigint
  calculateDailyBonusPoolTokensForVault: (tierIndex: number) => bigint
  totalBonusPoolTokensEmittedFromVaults: bigint
  totalStandardPoolTokensEmittedFromVaults: bigint
}

const StakingContext = createContext<StakingContextType | undefined>(undefined)

export const StakingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { wrongNetwork } = useNetworkId()
  const { getReadOnlyContract, getWriteOnlyContract } = useWeb3()
  const isLoggedIn = useIsLoggedInAndAuthenticated()
   const account = useSelector(selectAccount)
  const dispatch = useDispatch()
  const userInformation = useSelector(selectUser)

  const { data: universalVaultVestingInformation } =
    useGetUniversalVaultVestingInformationQuery({})

  const { data: sablierLockerContractData } = useGetContractByNameQuery({
    name: 'SablierV2LockupDynamic',
    chainId: BASE.chainId,
  })
  const sablierStreams = userInformation?.metadata?.sablierStreams
  const hasSablierStream = !!sablierStreams?.length
  const [pendingSablierBalances, setPendingSablierBalances] = useState<
    SablierBalanceAndContract[]
  >([])
  const hasPendingSablierBalances = !!pendingSablierBalances?.length
  const fetchSablierStreamBalances = useCallback(async () => {
    const { abi } = sablierLockerContractData?.contract ?? {}

    if (!abi || !isLoggedIn) return
    if (!sablierStreams?.length) return

    const balances = await Promise.all(
      sablierStreams?.map(async ({ streamId, lockerAddress, label }) => {
        // const useWallet =
        //   !!walletAddress &&
        //   !!account &&
        //   account?.toLowerCase() === walletAddress?.toLowerCase()
        //

        const readContract = getReadOnlyContract(lockerAddress, abi)
        const writeContract = getWriteOnlyContract(lockerAddress, abi)

        console.log({ streamId, lockerAddress, writeContract, readContract })
        const balance = await readContract.streamedAmountOf(streamId)
        console.log({ streamId, balance, writeContract, readContract, label })
        return {
          streamId,
          balance,
          lockerAddress,
          contract: writeContract,
          label,
        }
      }),
    )
    setPendingSablierBalances(balances)
  }, [
    sablierLockerContractData?.contract,
    isLoggedIn,
    sablierStreams,
    getReadOnlyContract,
    getWriteOnlyContract,
  ])

  useEffect(() => {
    if (!!hasPendingSablierBalances) return
    fetchSablierStreamBalances()
  }, [fetchSablierStreamBalances, hasPendingSablierBalances])

  const [sablierClaimStatus, setSablierClaimStatus] = useState<
    {
      status: 'idle' | 'claiming' | 'success' | 'error'
      amount?: bigint
    } & Partial<SablierBalanceAndContract>
  >({ status: 'idle' })
  const claimSablierStream = useCallback(
    async (stream: SablierBalanceAndContract) => {
      try {
        if (!account || wrongNetwork) return
        setSablierClaimStatus({ status: 'claiming', ...stream })
        const { streamId, contract, balance } = stream
        const transaction = await contract['withdrawMax'](streamId, account)
        const tx = await transaction.wait()
        if (!tx?.transactionHash) throw new Error('No transaction hash')
        toast.success('Withdrawal successful!')

        await fetchSablierStreamBalances()

        setSablierClaimStatus({
          status: 'success',
          amount: improvedAmountFromWei(balance),
          ...stream,
        })
      } catch (e) {
        setSablierClaimStatus({ status: 'error', ...stream })
        console.error('Error claiming sablier stream', e)
        toast.error('Something went wrong!')
      }
    },
    [account, fetchSablierStreamBalances, wrongNetwork],
  )

  const stakeInformation = useSelector(selectStakeInformation)
  const hasStakeInformation = !!stakeInformation
  const [fetchStakeInformation] = useLazyGetStakeInformationQuery()
  const fetchTierStakeInformation = useCallback(
    async (tierIndex: TierIndex) => {
      if (!isLoggedIn) return
      if (tierIndex < 0 || tierIndex > 2) return
      const { info } = await fetchStakeInformation({ tierIndex }).unwrap()
      if (!info) return
      const data = convertAllStringsInObjectToBigInt(info)
      dispatch(setStakeInformation({ tierIndex, stakeInformation: data }))
    },
    [isLoggedIn, fetchStakeInformation, dispatch],
  )

  const fetchAllTierStakeInformation = useCallback(async () => {
    try {
      if (!isLoggedIn) return
      if (!!stakeInformation) return
      await Promise.all(
        [0, 1, 2].map(async (tierIndex) =>
          fetchTierStakeInformation(tierIndex),
        ),
      )
    } catch (e) {
      console.error('Error fetching all tier stake information', e)
      toast.error('Something went wrong!')
    }
  }, [isLoggedIn, stakeInformation, fetchTierStakeInformation])

  useEffect(() => {
    if (!account) return
    fetchAllTierStakeInformation()
  }, [fetchAllTierStakeInformation, account])

  const [tokensStakeInformation, setTokensStakeInformation] = useState<
    TokensStakeInformation | undefined
  >()
  const hasTokensStakeInformation = !!tokensStakeInformation
  const [getTokensStakeInformation] = useLazyGetTokensStakeInformationQuery()

  const fetchTokensStakeInformation = useCallback(async () => {
    const { tokensStakeInformation: info } = await getTokensStakeInformation(
      {},
    ).unwrap()
    setTokensStakeInformation(info)
  }, [getTokensStakeInformation])

  useEffect(() => {
    if (!!hasTokensStakeInformation) return
    fetchTokensStakeInformation()
  }, [fetchTokensStakeInformation, hasTokensStakeInformation])

  const {
    data: zapStakingContractData,
    isLoading: isLoadingZapStakingContractData,
  } = useGetContractByNameQuery({ name: 'ZapStaking', chainId: BASE.chainId })
  const {
    data: zapRewardsContractData,
    isLoading: isLoadingZapRewardsContractData,
  } = useGetContractByNameQuery({ name: 'ZapRewards', chainId: BASE.chainId })
  const {
    data: vaultVestingContractData,
    isLoading: isLoadingVaultVestingContractData,
  } = useGetContractByNameQuery({ name: 'VaultVesting', chainId: BASE.chainId })
  const {
    data: zapTokenContractData,
    isLoading: isLoadingZapTokenContractData,
  } = useGetContractByNameQuery({ name: 'ZapToken', chainId: BASE.chainId })

  const zapStakingContractReadOnlyInstance = useGetContractInstanceReadOnly({
    contract: zapStakingContractData?.contract,
  })
  const zapRewardsContractReadOnlyInstance = useGetContractInstanceReadOnly({
    contract: zapRewardsContractData?.contract,
  })
  const vaultVestingContractReadOnlyInstance = useGetContractInstanceReadOnly({
    contract: vaultVestingContractData?.contract,
  })
  const zapTokenContractReadOnlyInstance = useGetContractInstanceReadOnly({
    contract: zapTokenContractData?.contract,
  })

  const zapStakingContractWriteOnlyInstance = useGetContractInstanceWriteOnly({
    contract: zapStakingContractData?.contract,
  })
  const zapRewardsContractWriteOnlyInstance = useGetContractInstanceWriteOnly({
    contract: zapRewardsContractData?.contract,
  })
  const vaultVestingContractWriteOnlyInstance = useGetContractInstanceWriteOnly(
    { contract: vaultVestingContractData?.contract },
  )
  const zapTokenContractWriteOnlyInstance = useGetContractInstanceWriteOnly({
    contract: zapTokenContractData?.contract,
  })

  const [vaultsState, setVaultsState] = useState({
    data: null,
    isLoadingVaultsState: false,
    vaultsStateError: null,
  })
  const hasVaultsState = !!vaultsState?.data

  const fetchVaultsState = useCallback(async () => {
    try {
      setVaultsState(() => ({
        data: null,
        isLoadingVaultsState: true,
        vaultsStateError: null,
      }))

      if (!vaultVestingContractReadOnlyInstance) return
      if (!account || !isLoggedIn) return
      if (wrongNetwork) return

      const userState = await vaultVestingContractReadOnlyInstance.userState(
        account,
      )

      setVaultsState({
        data: changeAllBigishToNativeBigInt(userState.toObject()),
        isLoadingVaultsState: false,
        vaultsStateError: null,
      })
    } catch (error) {
      console.error('Error getting vaults state: ', error)
      toast.error('Something went wrong. Please try again')
      setVaultsState((prev) => ({
        ...prev,
        isLoadingVaultsState: false,
        vaultsStateError: error,
      }))
    }
  }, [vaultVestingContractReadOnlyInstance, account, isLoggedIn, wrongNetwork])

  useEffect(() => {
    if (!account) return
    fetchVaultsState()
  }, [fetchVaultsState, account])

  const [userVaultStakes, setUserVaultStakes] = useState<UserVaultStake[]>([])
  const hasUserVaultStakes = !!userVaultStakes?.length

  const fetchUserVaultStakes = useCallback(async () => {
    try {
      if (!zapStakingContractReadOnlyInstance) return
      if (!account || !isLoggedIn) return
      if (wrongNetwork) return
      const result =
        await zapStakingContractReadOnlyInstance.getUserVaultStakes(account)
      setUserVaultStakes(result.map((stake) => stake.toObject()))
    } catch (e) {
      console.error('Error fetching user vault stakes', e)
      toast.error('Something went wrong!')
    }
  }, [zapStakingContractReadOnlyInstance, account, isLoggedIn, wrongNetwork])

  useEffect(() => {
    if (!account) return
    fetchUserVaultStakes()
  }, [fetchUserVaultStakes, account])

  const [tokenBalance, setTokenBalance] = useState<number | undefined>()
  const hasFetchedTokenBalance = tokenBalance !== undefined

  const fetchTokenBalance = useCallback(async () => {
    try {
      if (!zapTokenContractReadOnlyInstance) return
      if (!account || !isLoggedIn) return
      if (wrongNetwork) return
      const balance = await zapTokenContractReadOnlyInstance.balanceOf(account)
      setTokenBalance(Number(improvedAmountFromWei(balance)))
    } catch (e) {
      console.error('Error fetching token balance', e)
      toast.error('Something went wrong!')
    }
  }, [zapTokenContractReadOnlyInstance, account, isLoggedIn, wrongNetwork])

  useEffect(() => {
    if (!account) return
    fetchTokenBalance()
  }, [account, fetchTokenBalance])

  const [userRewardsState, setUserRewardsState] = useState<UserRewards | null>(
    null,
  )
  const hasUserRewardsState = !!userRewardsState

  const fetchUserRewards = useCallback(async () => {
    try {
      if (!zapRewardsContractReadOnlyInstance) return
      if (!account || !isLoggedIn) return
      if (wrongNetwork) return
      const result = await zapRewardsContractReadOnlyInstance.getUserRewards(
        account,
      )
      if (!result) return
      setUserRewardsState(
        changeAllBigishToNativeBigInt(result.toObject()) as UserRewards,
      )
    } catch (e) {
      console.error('Error fetching user rewards', e)
      toast.error('Something went wrong!')
    }
  }, [zapRewardsContractReadOnlyInstance, account, isLoggedIn, wrongNetwork])

  useEffect(() => {
    if (!account) return
    fetchUserRewards()
  }, [fetchUserRewards, account])

  const [userTokenStakes, setUserTokenStakes] = useState<UserTokenStake[]>([])
  const [tokensStaked, setTokensStaked] = useState(0)
  const hasUserTokenStakes = !!userTokenStakes?.length
  const fetchStakedTokens = useCallback(async () => {
    try {
      if (!zapStakingContractReadOnlyInstance) return
      if (!account || !isLoggedIn) return
      if (wrongNetwork) return
      const result =
        await zapStakingContractReadOnlyInstance.getUserTokenStakes(account)
      const properResult = result.map((stake) => stake.toObject())
      setUserTokenStakes(properResult)
      const total = properResult.reduce(
        (acc, { tokensStaked }) => acc + tokensStaked,
        0n,
      )
      setTokensStaked(Math.floor(amountFromWei(total).toNumber()))
    } catch (e) {
      console.error('Error fetching user token stakes', e)
      toast.error('Something went wrong!')
    }
  }, [zapStakingContractReadOnlyInstance, account, isLoggedIn, wrongNetwork])

  useEffect(() => {
    if (!account) return
    fetchStakedTokens()
  }, [fetchStakedTokens, account])

  const [tokensStakingStatus, setTokensStakingStatus] = useState<
    'idle' | 'approve' | 'staking' | 'success' | 'error'
  >('idle')

  const stakeTokens = useCallback(
    async (months: number, amount: number, autoRelock: boolean) => {
      if (!zapStakingContractWriteOnlyInstance) return
      if (!zapTokenContractWriteOnlyInstance) return

      try {
        setTokensStakingStatus('approve')

        const amountInWei = amountToWei(amount).toString()

        const zapStakingContractAddress =
          await zapStakingContractWriteOnlyInstance.getAddress()

        const approveGas = await zapTokenContractWriteOnlyInstance[
          'approve'
        ].estimateGas(zapStakingContractAddress, amountInWei)

        const approveTx = await zapTokenContractWriteOnlyInstance['approve'](
          zapStakingContractAddress,
          amountInWei,
          {
            gasLimit: approveGas,
          },
        )
        await approveTx.wait()

        setTokensStakingStatus('staking')

        const tierIndex = tierIndexMap[months]

        const args = [tierIndex, amountInWei, autoRelock]
        console.log({ args })
        const gas = await zapStakingContractWriteOnlyInstance[
          'stakeTokens'
        ].estimateGas(...args)
        console.log({ gas, args })
        if (!gas) throw new Error('No gas returned')

        const transaction = await zapStakingContractWriteOnlyInstance[
          'stakeTokens'
        ](...args, {
          gasLimit: gas * 2n,
        })

        const { hash: transactionHash } = transaction
        if (!transactionHash) throw new Error('No transaction hash')
        await transaction.wait()

        await fetchTokenBalance()
        await fetchStakedTokens()

        setTokensStakingStatus('success')
      } catch (e) {
        console.error('Error staking tokens', e)
        Sentry.captureException(e, { extra: { months, amount, autoRelock } })
        toast.error('Something went wrong!')
        setTokensStakingStatus('error')
        throw e
      }
    },
    [
      zapStakingContractWriteOnlyInstance,
      zapTokenContractWriteOnlyInstance,
      fetchTokenBalance,
      fetchStakedTokens,
    ],
  )

  const [vaultsStakingStatus, setVaultsStakingStatus] = useState<
    'idle' | 'staking' | 'success' | 'error'
  >('idle')
  const stakeVaults = useCallback(
    async (months: number, amount: number, autoRelock: boolean) => {
      if (!zapStakingContractWriteOnlyInstance) return

      try {
        setVaultsStakingStatus('staking')

        const tierIndex = tierIndexMap[months]

        const args = [tierIndex, amount, autoRelock]
        const gas = await zapStakingContractWriteOnlyInstance[
          'stakeVaults'
        ].estimateGas(...args)
        if (!gas) throw new Error('No gas returned')
        const transaction = await zapStakingContractWriteOnlyInstance[
          'stakeVaults'
        ](...args, {
          gasLimit: gas * 2n,
        })
        const { hash: transactionHash } = transaction
        await transaction.wait()

        await fetchTierStakeInformation(tierIndex)
        await fetchVaultsState()
        await fetchUserVaultStakes()

        toast.success('Vaults staked successfully!')
        setVaultsStakingStatus('success')
      } catch (e) {
        console.error('Error staking vaults', e)
        Sentry.captureException(e, { extra: { months, amount, autoRelock } })
        toast.error('Something went wrong!')
        setVaultsStakingStatus('error')
        throw e
      }
    },
    [
      zapStakingContractWriteOnlyInstance,
      fetchTierStakeInformation,
      fetchVaultsState,
      fetchUserVaultStakes,
    ],
  )

  const [vaultsUnstakingStatus, setVaultsUnstakingStatus] = useState<
    'idle' | 'unstaking' | 'success' | 'error'
  >('idle')
  const unstakeVaults = useCallback(
    async (tierIndex: number) => {
      if (!zapStakingContractWriteOnlyInstance) return

      try {
        setVaultsUnstakingStatus('unstaking')

        const args = [tierIndex]
        const gas = await zapStakingContractWriteOnlyInstance[
          'unstakeVaults'
        ].estimateGas(...args)
        if (!gas) throw new Error('No gas returned')
        const transaction = await zapStakingContractWriteOnlyInstance[
          'unstakeVaults'
        ](...args, {
          gasLimit: gas * 2n,
        })
        const { hash: transactionHash } = transaction
        await transaction.wait()

        await fetchTierStakeInformation(tierIndex)
        await fetchVaultsState()
        await fetchUserVaultStakes()

        toast.success('Vaults unstaked successfully!')
        setVaultsStakingStatus('success')
      } catch (e) {
        console.error('Error unstaking vaults', e)
        Sentry.captureException(e, { extra: { tierIndex } })
        toast.error('Something went wrong!')
        setVaultsStakingStatus('error')
        throw e
      }
    },
    [
      zapStakingContractWriteOnlyInstance,
      fetchTierStakeInformation,
      fetchVaultsState,
      fetchUserVaultStakes,
    ],
  )

  const [tokensUnstakingStatus, setTokensUnstakingStatus] = useState<
    'idle' | 'unstaking' | 'success' | 'error'
  >('idle')
  const unstakeTokens = useCallback(
    async (tierIndex: number) => {
      if (!zapStakingContractWriteOnlyInstance) return

      try {
        setTokensUnstakingStatus('unstaking')

        const args = [tierIndex]
        const gas = await zapStakingContractWriteOnlyInstance[
          'unstakeTokens'
        ].estimateGas(...args)
        if (!gas) throw new Error('No gas returned')
        const transaction = await zapStakingContractWriteOnlyInstance[
          'unstakeTokens'
        ](...args, {
          gasLimit: gas * 2n,
        })
        const { hash: transactionHash } = transaction
        await transaction.wait()

        await fetchTierStakeInformation(tierIndex)
        await fetchStakedTokens()
        await fetchTokenBalance()

        toast.success('Tokens unstaked successfully!')
        setTokensUnstakingStatus('success')
      } catch (e) {
        console.error('Error unstaking tokens', e)
        Sentry.captureException(e, { extra: { tierIndex } })
        toast.error('Something went wrong!')
        setTokensUnstakingStatus('error')
        throw e
      }
    },
    [
      zapStakingContractWriteOnlyInstance,
      fetchTierStakeInformation,
      fetchStakedTokens,
      fetchTokenBalance,
    ],
  )

  const getEarlyUnstakeFee = useCallback(
    async (tokensStaked: bigint, timeStaked: number, expiresAt: number) => {
      if (!zapStakingContractReadOnlyInstance) return
      if (!account || !isLoggedIn) return
      if (wrongNetwork) return

      const now = Math.floor(Date.now() / 1000)
      try {
        const args = [
          improvedAmountToWei(tokensStaked),
          timeStaked,
          expiresAt,
          now,
        ]
        const unstakeFee =
          await zapStakingContractReadOnlyInstance.calculateEarlyUnstakeFee(
            ...args,
          )

        return unstakeFee
      } catch (e) {
        console.error('Error getting early unstake fee', e)
        throw e
      }
    },
    [account, isLoggedIn, wrongNetwork, zapStakingContractReadOnlyInstance],
  )

  const [vestClaimedVaultsStatus, setVestClaimedVaultsStatus] = useState<{
    status: 'idle' | 'vesting' | 'success' | 'error'
    amount?: bigint
  }>({ status: 'idle' })
  const vestClaimedVaults = useCallback(
    async (amount: bigint) => {
      if (!vaultVestingContractWriteOnlyInstance) return

      try {
        setVestClaimedVaultsStatus({ status: 'vesting' })

        const args = []

        const gas = await vaultVestingContractWriteOnlyInstance[
          'claimVestedTokensForClaimedVaults'
        ].estimateGas(...args)
        if (!gas) throw new Error('No gas returned')
        const transaction = await vaultVestingContractWriteOnlyInstance[
          'claimVestedTokensForClaimedVaults'
        ](...args, {
          gasLimit: gas * 2n,
        })
        await transaction.wait()

        await fetchTokenBalance()
        await fetchUserRewards()
        await fetchVaultsState()

        toast.success('Tokens vested successfully!')
        setVestClaimedVaultsStatus({ status: 'success', amount })
      } catch (e) {
        console.error('Error vesting tokens', e)
        Sentry.captureException(e, { extra: { amount: 42069 } })
        toast.error('Something went wrong!')
        setVestClaimedVaultsStatus({ status: 'error' })
      }
    },
    [
      vaultVestingContractWriteOnlyInstance,
      fetchTokenBalance,
      fetchUserRewards,
      fetchVaultsState,
    ],
  )

  const [rewardsClaimStatus, setRewardsClaimStatus] = useState<{
    status: 'idle' | 'claiming' | 'success' | 'error'
    amount?: bigint
  }>({ status: 'idle' })
  const claimRewards = useCallback(
    async (amount: bigint) => {
      if (!zapRewardsContractWriteOnlyInstance) return

      try {
        setRewardsClaimStatus({ status: 'claiming' })

        const args = []
        const gas = await zapRewardsContractWriteOnlyInstance[
          'claimRewards'
        ].estimateGas(...args)
        if (!gas) throw new Error('No gas returned')
        const transaction = await zapRewardsContractWriteOnlyInstance[
          'claimRewards'
        ](...args, {
          gasLimit: gas * 2n,
        })
        await transaction.wait()

        await fetchTokenBalance()
        await fetchUserRewards()
        await fetchVaultsState()

        toast.success('Rewards claimed successfully!')
        setRewardsClaimStatus({ status: 'success', amount })
      } catch (e) {
        console.error('Error claiming rewards', e)
        Sentry.captureException(e)
        toast.error('Something went wrong!')
        setRewardsClaimStatus({ status: 'error' })
      }
    },
    [
      zapRewardsContractWriteOnlyInstance,
      fetchTokenBalance,
      fetchUserRewards,
      fetchVaultsState,
    ],
  )

  const [vaultsClaimStatus, setVaultsClaimStatus] = useState<
    'idle' | 'claiming' | 'success' | 'error'
  >('idle')
  const claimVaults = useCallback(
    async (amount: number) => {
      if (!vaultVestingContractWriteOnlyInstance) return

      try {
        setVaultsClaimStatus('claiming')

        const args = [amount]
        const gas = await vaultVestingContractWriteOnlyInstance[
          'claimVaults'
        ].estimateGas(...args)
        if (!gas) throw new Error('No gas returned')
        const transaction = await vaultVestingContractWriteOnlyInstance[
          'claimVaults'
        ](...args, {
          gasLimit: gas * 2n,
        })
        await transaction.wait()

        await fetchTokenBalance()
        await fetchVaultsState()

        setVaultsClaimStatus('success')
      } catch (e) {
        console.error('Error claiming vaults', e)
        Sentry.captureException(e, { extra: { amount } })
        toast.error('Something went wrong!')
        setVaultsClaimStatus('error')
        throw e
      }
    },
    [
      vaultVestingContractWriteOnlyInstance,
      fetchTokenBalance,
      fetchVaultsState,
    ],
  )

  const resetStatusByKind = useCallback(
    (
      kind:
        | 'vaults-claim'
        | 'vaults-staking'
        | 'tokens-staking'
        | 'tokens-staked'
        | 'rewards-claimed'
        | 'vest-claimed'
        | 'sablier-claimed',
    ) => {
      const fn = {
        'vaults-claim': () => setVaultsClaimStatus('idle'),
        'vaults-staking': () => setVaultsStakingStatus('idle'),
        'tokens-staking': () => setTokensStakingStatus('idle'),
        'tokens-staked': () => setTokensStaked(0),
        'rewards-claimed': () => setRewardsClaimStatus({ status: 'idle' }),
        'vest-claimed': () => setVestClaimedVaultsStatus({ status: 'idle' }),
        'sablier-claimed': () => setSablierClaimStatus({ status: 'idle' }),
      }[kind]
      if (fn) fn()
    },
    [],
  )

  const userVaultsState = vaultsState?.data
  const isLoadingUserVaultsState = vaultsState.isLoadingVaultsState
  const userVaultsStateError = vaultsState.vaultsStateError

  const {
    vaultsHeld,
    vaultsClaimed,
    vaultsStaked,
    standardPoolTokensLocked,
    standardPoolTokensClaimed,
    standardPoolTokensUnlocked,
    bonusPoolTokensLocked,
    bonusPoolTokensUnlocked,
    bonusPoolTokensClaimed,
  } = userVaultsState ?? ({} as any) // TODO: type this I guess lol

  const vaultsAvailable =
    (vaultsHeld ?? 0) - ((vaultsClaimed ?? 0) + (vaultsStaked ?? 0))

  const hasClaimableVaults = vaultsStaked < vaultsHeld

  const tokensLocked = improvedAmountFromWei(
    bigishToNativeBigInt(standardPoolTokensLocked) + // yo if we add inline before converting we add string + string
      bigishToNativeBigInt(bonusPoolTokensLocked), // hence what happened before - nothing wrong with the functions lol
  )

  const tokensUnlockedFromVesting = improvedAmountFromWei(
    bigishToNativeBigInt(standardPoolTokensUnlocked) +
      bigishToNativeBigInt(bonusPoolTokensUnlocked),
  )

  const tokensClaimedFromVesting = improvedAmountFromWei(
    bigishToNativeBigInt(standardPoolTokensClaimed) +
      bigishToNativeBigInt(bonusPoolTokensClaimed),
  )

  const tokensAvailable = Math.floor(tokenBalance)

  const totalTokensVested = tokensUnlockedFromVesting
  const totalTokensAccrued = tokensLocked + tokensUnlockedFromVesting

  const tokensClaimableFromVesting =
    tokensUnlockedFromVesting - tokensClaimedFromVesting

  const tokensEarnedFromRewards = improvedAmountFromWei(
    bigishToNativeBigInt(userRewardsState?.bonusPoolTokensAccrued) +
      bigishToNativeBigInt(userRewardsState?.standardPoolTokensAccrued),
  )

  const tokensClaimedFromRewards = improvedAmountFromWei(
    bigishToNativeBigInt(userRewardsState?.bonusPoolTokensClaimed) +
      bigishToNativeBigInt(userRewardsState?.standardPoolTokensClaimed),
  )

  const tokensClaimableFromRewards =
    tokensEarnedFromRewards - tokensClaimedFromRewards

  const claimableTokens =
    tokensClaimableFromRewards + tokensClaimableFromVesting

  const totalTokensClaimed = tokensClaimedFromRewards + tokensClaimedFromVesting

  const stakedTokensPercentage =
    !tokensAvailable && !tokensStaked
      ? 0
      : (Number(tokensStaked) /
          (Number(tokensAvailable) + Number(tokensStaked))) *
        100
  const stakedVaultsPercentage = !vaultsStaked
    ? 0
    : (Number(vaultsStaked) / Number(vaultsHeld)) * 100
  const claimedVaultsPercentage = !vaultsClaimed
    ? 0
    : (Number(vaultsClaimed) / Number(vaultsHeld)) * 100

  // TODO: remove below true on prod
  const useGuestVaultCalc = !account || true // you can test here
  // const useGuestVaultCalc = !account
  const typicalVaultHolder =
    universalVaultVestingInformation?.data?.typicalVaultHolder

  const dailyEmissionsPerVault = BigInt(
    (useGuestVaultCalc
      ? typicalVaultHolder?.currentTotalDailyEmissionRatePerVault
      : userVaultsState?.currentTotalDailyEmissionRatePerVault) ?? 0,
  )

  const alreadyAccruedTokensPerVault =
    vaultsHeld > 0 && vaultsHeld > vaultsClaimed
      ? totalTokensAccrued / (BigInt(vaultsHeld) - BigInt(vaultsClaimed))
      : BigInt(0)

  const estimatedTokensPerVault = dailyEmissionsPerVault

  const getEstimatedTokensPerVaultOverStake = useCallback(
    (tier: number) => {
      const stakeDuration = getTierDuration(tier)

      const tokensOverStake = calculateEstimatedTokensOverVaultStake(
        dailyEmissionsPerVault,
        BigInt(stakeDuration) * 24n * 60n * 60n, // takes seconds
        1n,
        universalVaultVestingInformation?.data,
      )
      return tokensOverStake + improvedAmountToWei(alreadyAccruedTokensPerVault)
    },
    [
      dailyEmissionsPerVault,
      universalVaultVestingInformation,
      alreadyAccruedTokensPerVault,
    ],
  )

  const getTierApy = useCallback(
    ({ tokensAmount, tier, target, totalTokensStaked }) => {
      if (!totalTokensStaked) return 0
      const tierDuration = getTierDuration(tier)
      const dailyEmission = getDailyStandardPoolRewardsForStake(
        tokensAmount,
        target === 'tokens' ? getTokenMultiplier(tier) : 1,
        totalTokensStaked,
      )
      const apy =
        (Number(dailyEmission) * Number(tierDuration)) / Number(tokensAmount)
      return apy * 100
    },
    [],
  )

  const getEstimatedExtraTokens = useCallback(
    ({ tokensAmount, tier, target, totalTokensStaked }) => {
      const tierDuration = {
        0: 90,
        1: 180,
        2: 360,
      }

      const dailyEmission = getDailyStandardPoolRewardsForStake(
        tokensAmount,
        target === 'tokens' ? getTokenMultiplier(tier) : 1,
        totalTokensStaked,
      )

      const duration = tierDuration[tier]

      return dailyEmission * duration
    },
    [],
  )

  // bro totalStandardWeighting is fixed :skull"
  const { totalStandardWeighting: _totalTokensStaked } =
    tokensStakeInformation?.[0] ?? {}
  const totalTokensStaked = BigInt(_totalTokensStaked ?? 0)

  const tier0TokenApy = calculateBaseAPY({
    dailyRewards,
    stakedAmount: 1,
    totalTokensStaked,
    tier: 0,
  })

  const tier1TokenApy = calculateBaseAPY({
    dailyRewards,
    stakedAmount: 1,
    totalTokensStaked,
    tier: 1,
  })

  const tier2TokenApy = calculateBaseAPY({
    dailyRewards,
    stakedAmount: 1,
    totalTokensStaked,
    tier: 2,
  })

  const tokenApy = useMemo(
    () => ({ 0: tier0TokenApy, 1: tier1TokenApy, 2: tier2TokenApy }),
    [tier0TokenApy, tier1TokenApy, tier2TokenApy],
  )
  const vaultMultipier = useMemo(() => ({ 0: 11, 1: 13, 2: 15 }), [])

  const calculateStandardPoolTokensOverStakePerVault = useCallback(
    (tier: number) => {
      if (!typicalVaultHolder) return 0n

      const dailyEmissionsPerVault = BigInt(
        useGuestVaultCalc
          ? typicalVaultHolder.currentDatalDailyEmissionRatePerVault
          : userVaultsState?.currentDatalDailyEmissionRatePerVault,
      )

      const lengthOfStake = getTierDuration(tier)

      const tokensOverStake = calculateEstimatedTokensOverVaultStake(
        dailyEmissionsPerVault,
        BigInt(lengthOfStake) * 24n * 60n * 60n, // takes seconds
        1n,
        universalVaultVestingInformation?.data,
      )

      return tokensOverStake
    },
    [
      typicalVaultHolder,
      universalVaultVestingInformation?.data,
      useGuestVaultCalc,
      userVaultsState?.currentDatalDailyEmissionRatePerVault,
    ],
  )

  const calculateDailyBonusPoolTokensForVault = useCallback(
    (tier: number) => {
      if (!typicalVaultHolder) return 0n

      const tokensEarnedPreTGE = BigInt(
        useGuestVaultCalc
          ? typicalVaultHolder.tokensEarnedPreTGE
          : userVaultsState?.tokensEarnedPreTGE,
      )

      const tokensPreTGEPerVault = BigInt(
        tokensEarnedPreTGE / BigInt(useGuestVaultCalc ? 1 : vaultsHeld),
      )

      if (!totalTokensStaked) return 0n
      if (!tokensEarnedPreTGE || tokensEarnedPreTGE === 0n) return 0n

      const bonusPoolEmissionsWithoutMultiplier =
        (BigInt(tokensPreTGEPerVault) * BigInt(dailyRewards)) /
        BigInt(totalTokensStaked)

      const multiplier = vaultMultipier[tier]
      if (!multiplier) return 0n

      const bonusPoolEmissionsWithMultiplier =
        (BigInt(tokensPreTGEPerVault) *
          BigInt(dailyRewards) *
          BigInt(vaultMultipier[tier])) /
        10n /
        BigInt(totalTokensStaked)

      const bonusPoolEmissionsPerDay = BigInt(
        bonusPoolEmissionsWithMultiplier - bonusPoolEmissionsWithoutMultiplier,
      )

      return bonusPoolEmissionsPerDay
    },
    [
      typicalVaultHolder,
      useGuestVaultCalc,
      userVaultsState?.tokensEarnedPreTGE,
      vaultsHeld,
      totalTokensStaked,
      vaultMultipier,
    ],
  )

  const calculateBonusApy = useCallback(
    (tier: number) => {
      if (!typicalVaultHolder) return 0

      const tokensEarnedPreTGE = BigInt(
        useGuestVaultCalc
          ? typicalVaultHolder?.tokensEarnedPreTGE ?? 0
          : userVaultsState?.tokensEarnedPreTGE ?? 0,
      )

      const tokensPreTGEPerVault = BigInt(
        tokensEarnedPreTGE / BigInt(useGuestVaultCalc ? 1 : vaultsHeld),
      )

      if (!totalTokensStaked) return 0
      if (!tokensEarnedPreTGE || tokensEarnedPreTGE === 0n) return 0

      const bonusPoolEmissionsPerDay =
        calculateDailyBonusPoolTokensForVault(tier)

      const lengthOfStake = getTierDuration(tier)

      const tokensOverStake =
        calculateEstimatedTokensOverVaultStake(
          dailyEmissionsPerVault,
          BigInt(lengthOfStake) * 24n * 60n * 60n, // takes seconds
          1n,
          universalVaultVestingInformation?.data,
        ) + tokensPreTGEPerVault

      const bonusApy =
        ((Number(bonusPoolEmissionsPerDay) * 365) / Number(tokensOverStake)) *
        100

      return bonusApy
    },
    [
      typicalVaultHolder,
      useGuestVaultCalc,
      userVaultsState?.tokensEarnedPreTGE,
      vaultsHeld,
      totalTokensStaked,
      calculateDailyBonusPoolTokensForVault,
      dailyEmissionsPerVault,
      universalVaultVestingInformation?.data,
    ],
  )

  const {
    totalBonusPoolTokensEmittedFromVaults,
    totalStandardPoolTokensEmittedFromVaults,
  } = useMemo(() => {
    let bonusPoolTokens = 0n
    let standardPoolTokens = 0n

    if (!!userVaultsState) {
      bonusPoolTokens =
        userVaultsState.bonusPoolTokensLocked +
        userVaultsState.bonusPoolTokensUnlocked
      standardPoolTokens =
        userVaultsState.standardPoolTokensLocked +
        userVaultsState.standardPoolTokensUnlocked
    }

    return {
      totalStandardPoolTokensEmittedFromVaults: standardPoolTokens,
      totalBonusPoolTokensEmittedFromVaults: bonusPoolTokens,
    }
  }, [userVaultsState])

  const contextValue = useMemo(
    () => ({
      getTierApy,
      getEstimatedExtraTokens,
      stakeInformation,
      tokensStakeInformation,
      fetchVaultsState,
      userVaultsState,
      isLoadingUserVaultsState,
      userVaultsStateError,
      userVaultStakes,
      userTokenStakes,
      hasClaimableVaults,
      vaultsClaimed,
      vaultsAvailable,
      estimatedTokensPerVault,
      stakedVaultsPercentage,
      tokensAvailable,
      tokensStaked,
      claimableTokens,
      totalTokensClaimed,
      totalTokensVested,
      totalTokensAccrued,
      stakedTokensPercentage,
      stakeVaults,
      vaultsStakingStatus,
      getEarlyUnstakeFee,
      unstakeVaults,
      vaultsUnstakingStatus,
      claimVaults,
      vaultsClaimStatus,
      stakeTokens,
      tokensStakingStatus,
      unstakeTokens,
      tokensUnstakingStatus,
      tokensClaimableFromRewards,
      tokensClaimableFromVesting,
      vestClaimedVaultsStatus,
      vestClaimedVaults,
      rewardsClaimStatus,
      claimRewards,
      hasSablierStream,
      pendingSablierBalances,
      fetchSablierStreamBalances,
      claimSablierStream,
      sablierClaimStatus,
      fetchAllTierStakeInformation,
      resetStatusByKind,
      dailyRewards,
      tokenApy,
      totalTokensStaked,
      universalVaultVestingInformation,
      calculateBonusApy,
      calculateStandardPoolTokensOverStakePerVault,
      getEstimatedTokensPerVaultOverStake,
      calculateDailyBonusPoolTokensForVault,
      vaultsStaked,
      vaultsHeld,
      tokensClaimedFromRewards,
      tokensClaimedFromVesting,
      totalBonusPoolTokensEmittedFromVaults,
      totalStandardPoolTokensEmittedFromVaults,
    }),
    [
      getTierApy,
      getEstimatedExtraTokens,
      stakeInformation,
      tokensStakeInformation,
      fetchVaultsState,
      userVaultsState,
      isLoadingUserVaultsState,
      userVaultsStateError,
      userVaultStakes,
      userTokenStakes,
      hasClaimableVaults,
      vaultsClaimed,
      vaultsAvailable,
      estimatedTokensPerVault,
      stakedVaultsPercentage,
      tokensAvailable,
      tokensStaked,
      claimableTokens,
      totalTokensClaimed,
      totalTokensVested,
      totalTokensAccrued,
      stakedTokensPercentage,
      stakeVaults,
      vaultsStakingStatus,
      getEarlyUnstakeFee,
      unstakeVaults,
      vaultsUnstakingStatus,
      claimVaults,
      vaultsClaimStatus,
      stakeTokens,
      tokensStakingStatus,
      unstakeTokens,
      tokensUnstakingStatus,
      tokensClaimableFromRewards,
      tokensClaimableFromVesting,
      vestClaimedVaultsStatus,
      vestClaimedVaults,
      rewardsClaimStatus,
      claimRewards,
      hasSablierStream,
      pendingSablierBalances,
      claimSablierStream,
      sablierClaimStatus,
      fetchSablierStreamBalances,
      fetchAllTierStakeInformation,
      resetStatusByKind,
      tokenApy,
      totalTokensStaked,
      universalVaultVestingInformation,
      calculateBonusApy,
      calculateStandardPoolTokensOverStakePerVault,
      calculateDailyBonusPoolTokensForVault,
      getEstimatedTokensPerVaultOverStake,
      vaultsStaked,
      vaultsHeld,
      tokensClaimedFromRewards,
      tokensClaimedFromVesting,
      totalBonusPoolTokensEmittedFromVaults,
      totalStandardPoolTokensEmittedFromVaults,
    ],
  )

  return (
    <StakingContext.Provider value={contextValue}>
      {children}
    </StakingContext.Provider>
  )
}

export const useStakingContext = (): StakingContextType => {
  const context = useContext(StakingContext)
  if (context === undefined) {
    throw new Error('useStakingContext must be used within a StakingProvider')
  }
  return context
}

export function improvedAmountToWei(
  amount: string | number | Big | bigint | undefined | ethers.BigNumberish,
  decimals = 18,
) {
  if (!amount) return 0n
  const nativeJSVersion = bigishToNativeBigInt(amount)
  return nativeJSVersion * BigInt(10 ** decimals)
}

export function improvedAmountFromWei(
  amount: string | number | Big | bigint | undefined | ethers.BigNumberish,
  decimals = 18,
) {
  if (!amount) return 0n

  const nativeJSVersion = bigishToNativeBigInt(amount)
  return nativeJSVersion / BigInt(10 ** decimals)
}

export function improvedAmountFromWeiFloat(
  amount: string | number | Big | bigint | undefined | ethers.BigNumberish,
  decimals = 18,
) {
  if (!amount) return 0

  const nativeJSVersion = bigishToNativeBigInt(amount)
  return Number(nativeJSVersion) / Number(BigInt(10 ** decimals))
}

export function changeAllBigishToNativeBigInt(
  obj: Record<
    string,
    string | number | Big | bigint | ethers.BigNumberish | undefined
  >,
) {
  if (!obj) return undefined
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      bigishToNativeBigInt(value),
    ]),
  )
}

export function bigishToNativeBigInt(
  bigish?: string | number | Big | bigint | ethers.BigNumberish | undefined,
) {
  const isBigInt = typeof bigish === 'bigint'
  if (!bigish) return BigInt(0)

  const bigJSVersion = new Big(
    isBigInt ? '42069' : bigish?.toString() ?? (bigish as string),
  )
  const nativeJSVersion = isBigInt ? bigish : BigInt(bigJSVersion.toString())
  return nativeJSVersion
}

type TypicalVaultHolder = {
  vaultsHeld: string
  tokensEarnedPreTGE: string
  currentTotalDailyEmissionRatePerVault: string
}

export type VaultVestingUniversalInformation = {
  daysRemainingOfVesting: number
  currentTotalDailyTokensVested: string
  tokensVestedAtNextHalvingPeriod: string
  daysUntilNextHalvingEvent: number
  typicalVaultHolder: TypicalVaultHolder
}

export function calculateEstimatedTokensOverVaultStake(
  dailyEmissionPerVault: bigint,
  remainingTimeInStake: bigint,
  vaultsInStake: bigint,
  universalVaultVestingData: VaultVestingUniversalInformation,
) {
  if (vaultsInStake === 0n) return 0n

  const {
    daysRemainingOfVesting,
    currentTotalDailyTokensVested,
    tokensVestedAtNextHalvingPeriod,
    daysUntilNextHalvingEvent,
  } = convertAllStringsInObjectToBigInt(universalVaultVestingData)

  let daysLeftInStake = BigInt(remainingTimeInStake / (60n * 60n * 24n))

  if (daysRemainingOfVesting < daysLeftInStake)
    daysLeftInStake = daysRemainingOfVesting

  let estimatedTokensOverStake = 0n

  console.log({
    daysUntilNextHalvingEvent,
    daysLeftInStake,
    dailyEmissionPerVault,
    vaultsInStake,
    tokensVestedAtNextHalvingPeriod,
    currentTotalDailyTokensVested,
  })

  if (daysUntilNextHalvingEvent < daysLeftInStake) {
    const forecastedTotalForCurrentEmission =
      BigInt(daysUntilNextHalvingEvent) * dailyEmissionPerVault * vaultsInStake

    const postHalvingEventEmissions =
      BigInt(daysLeftInStake - daysUntilNextHalvingEvent) *
      ((dailyEmissionPerVault *
        vaultsInStake *
        tokensVestedAtNextHalvingPeriod) /
        currentTotalDailyTokensVested)

    estimatedTokensOverStake =
      forecastedTotalForCurrentEmission + postHalvingEventEmissions
  } else {
    estimatedTokensOverStake =
      dailyEmissionPerVault * BigInt(daysLeftInStake) * vaultsInStake
  }

  return estimatedTokensOverStake
}

export function calculateBaseAPY({
  stakedAmount,
  totalTokensStaked,
  dailyRewards,
  tier,
}: {
  stakedAmount: number
  totalTokensStaked: number | bigint
  dailyRewards: number
  tier: TierIndex
}) {
  if (dailyRewards === 0) return 0
  if (stakedAmount === 0) return 0

  const tokensMultiplier = getTokenMultiplier(tier)

  const dailyEmission = getDailyStandardPoolRewardsForStake(
    stakedAmount,
    tokensMultiplier,
    totalTokensStaked,
  )

  const apy = ((dailyEmission * 365) / stakedAmount) * 100

  return apy > 200 ? 200 : Number(apy.toFixed(2))
}

export function getDailyStandardPoolRewardsForStake(
  stakedAmount: number,
  tokensMultiplier: number,
  totalTokensStaked: number | bigint,
) {
  if (!totalTokensStaked) return 0
  return (
    ((Number(stakedAmount) * Number(tokensMultiplier)) /
      Number(totalTokensStaked)) *
    dailyRewards
  ) // GoHorse hardcoded
}

const getTierDuration = (tier: number) => {
  return {
    undefined: 0,
    0: 90,
    1: 180,
    2: 360,
  }[tier]
}

export function getTokenMultiplier(tier: number) {
  const tokensMultiplier = {
    0: 0.5,
    1: 0.7,
    2: 1,
  }[tier]
  return tokensMultiplier
}
